import { StitchingManipulationRules } from '../../../shared/Helpers';
import { ShapePoint } from '../../AnnotationManager/Annotation/annotation.types';

export type Sequence = {
  id: string;
  isStitched: boolean;
  isRejected: boolean;
  images: SequenceImage[];
  stitchingManipulationRules?: StitchingManipulationRules;
  subsampleFactor: number;
};

export type SequenceImage = {
  height: number;
  left: number;
  name: string;
  rotation: number;
  scale: number;
  src: string;
  top: number;
  width: number;
  isHidden: boolean;
  isHomographyValid?: boolean;
  stackingRank: number;
  stitchingManipulationRule: StitchingManipulationRules;
};

export type SequenceOverlap = {
  imageFileName: string;
  shape: Array<ShapePoint>;
  isVisible: boolean;
};

export type ImagesResponse = {
  images: SequenceImage[];
  stitchingManipulationRules: StitchingManipulationRules;
  subsampleFactor: number;
};

export type State = {
  selectedBlade: string;
  selectedBladeSide: string;
  images: SequenceImage[];
  subsampleFactor: number;
  isStitchingFinished: boolean;
  isBladeSideStitched: boolean;
  isThumbnailsCreationInProgress: boolean;
  areImagesAvailable: boolean;
  completedSides: string[];
  busy: boolean;
  isLoadingImages: boolean;
  isToolbarBusy: boolean;
  sequencesCache: Sequence[];
  isBladeSideRejected: boolean;
  canStitch: boolean;
  loading: boolean;
  totalNumberOfSequences: number;
};

const initialState: State = {
  selectedBlade: '',
  selectedBladeSide: '',
  images: [],
  subsampleFactor: 0,
  isStitchingFinished: false,
  isBladeSideStitched: false,
  isThumbnailsCreationInProgress: false,
  areImagesAvailable: false,
  completedSides: [],
  busy: false,
  isLoadingImages: false,
  isToolbarBusy: false,
  sequencesCache: [],
  isBladeSideRejected: false,
  canStitch: false,
  loading: false,
  totalNumberOfSequences: 0
};

export type Action =
  | {
      type: 'SET_IMAGES_AND_CACHE';
      images: SequenceImage[];
      subsampleFactor: number;
      newCache: Sequence;
    }
  | { type: 'ON_SELECT_BLADE_SIDE'; blade: string; side: string }
  | { type: 'SET_SEQUENCE_FROM_CACHE'; cacheId: string }
  | { type: 'REJECT_SEQUENCE' }
  | { type: 'CLEAR_BUSY_STATUS' }
  | { type: 'ON_CANVAS_READY' }
  | { type: 'SET_COMPLETED_SEQUENCES'; completedSides: string[]; totalNumberOfSequences: number }
  | {
      type: 'SET_SEQUENCE_INFO';
      inProgress: boolean;
      areImagesPrepared: boolean;
      isStitched: boolean;
      isRejected: boolean;
    }
  | { type: 'ON_SAVING_IMAGE_STARTED' }
  | {
      type: 'ON_SAVING_IMAGE_FINISHED';
      isStitchingFinished: boolean;
      finish: boolean;
      images: any[];
    }
  | {
      type: 'SET_CAN_STITCH';
      canStitch: boolean;
    }
  | {
      type: 'SET_LOADING';
      loading: boolean;
    };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_IMAGES_AND_CACHE': {
      const { images, subsampleFactor, newCache } = action;
      const { sequencesCache } = state;
      return {
        ...state,
        images,
        subsampleFactor,
        busy: false,
        areImagesAvailable: true,
        isLoadingImages: true,
        sequencesCache: [...sequencesCache, newCache]
      };
    }
    case 'CLEAR_BUSY_STATUS': {
      return { ...state, busy: false };
    }
    case 'ON_SELECT_BLADE_SIDE':
      const { blade: selectedBlade, side: selectedBladeSide } = action;
      return {
        ...state,
        selectedBlade,
        selectedBladeSide,
        busy: true,
        isThumbnailsCreationInProgress: false,
        images: [],
        areImagesAvailable: false
      };
    case 'SET_SEQUENCE_FROM_CACHE': {
      const { cacheId } = action;
      const { sequencesCache } = state;
      const cachedSequence = [...sequencesCache].find(x => x.id === cacheId);
      const { images, isStitched, isRejected, subsampleFactor } = cachedSequence!;
      return {
        ...state,
        images,
        subsampleFactor,
        busy: false,
        areImagesAvailable: true,
        isLoadingImages: true,
        isBladeSideRejected: isRejected,
        isBladeSideStitched: isStitched
      };
    }
    case 'SET_SEQUENCE_INFO': {
      const { inProgress, areImagesPrepared, isStitched: isBladeSideStitched, isRejected: isBladeSideRejected } = action;

      let newState = {
        ...state,
        isThumbnailsCreationInProgress: inProgress,
        busy: inProgress && !areImagesPrepared
      };

      if (areImagesPrepared) {
        return {
          ...newState,
          isBladeSideStitched,
          isBladeSideRejected
        };
      }

      return newState;
    }
    case 'REJECT_SEQUENCE': {
      const { sequencesCache, selectedBlade, selectedBladeSide } = state;
      const cacheId = `${selectedBlade}/${selectedBladeSide}`;
      return {
        ...state,
        isThumbnailsCreationInProgress: true,
        areImagesAvailable: false,
        isBladeSideRejected: true,
        images: [],
        busy: true,
        sequencesCache: [...sequencesCache.filter(x => x.id !== cacheId)]
      };
    }
    case 'ON_CANVAS_READY': {
      return { ...state, isLoadingImages: false };
    }
    case 'SET_COMPLETED_SEQUENCES': {
      const { completedSides, totalNumberOfSequences } = action;
      return { ...state, completedSides, totalNumberOfSequences };
    }
    case 'ON_SAVING_IMAGE_STARTED': {
      return { ...state, isLoadingImages: true, isToolbarBusy: true };
    }
    case 'ON_SAVING_IMAGE_FINISHED': {
      const { isStitchingFinished, finish, images } = action;
      const { completedSides, selectedBlade, selectedBladeSide, sequencesCache, totalNumberOfSequences } = state;
      const cacheId = `${selectedBlade}/${selectedBladeSide}`;
      //todo check if some action on cheched seq is needed!
      const cachedSequence = [...sequencesCache].find(x => x.id === cacheId);

      if (cachedSequence) {
        for (const image of images) {
          let cachedImage = cachedSequence.images.find(x => x.name === image.name);
          if (cachedImage) {
            image.src = cachedImage.src;
          }
        }

        cachedSequence.images = images;
        cachedSequence.isStitched = finish;
      }

      return {
        ...state,
        isStitchingFinished,
        isBladeSideStitched: finish,
        isToolbarBusy: false,
        completedSides: finish ? [...completedSides, `${selectedBlade}/${selectedBladeSide}`] : [...completedSides],
        totalNumberOfSequences: finish ? totalNumberOfSequences + 1 : totalNumberOfSequences,
        images,
        sequencesCache: [...sequencesCache.filter(x => x.id !== cacheId), cachedSequence!]
      };
    }
    case 'SET_CAN_STITCH': {
      const { canStitch } = action;
      return {
        ...state,
        canStitch
      };
    }
    case 'SET_LOADING': {
      const { loading } = action;
      return {
        ...state,
        loading
      };
    }
  }
};

export { reducer, initialState };
